import { DatabaseRecord } from '@agingplan';
import { HttpClient } from '@angular/common/http';
import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Delta } from 'quill';
import { firstValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { QuillComponent } from '../../../components/quill/quill.component';
import { OurDataService } from '../../our-data.service';
import { OpensOurDataModals } from '../opens-our-data-modals';

@Component({
  selector: 'ourdata-wiki',
  templateUrl: './wiki.component.html',
  styleUrls: ['./wiki.component.scss'],
})
export class WikiComponent extends OpensOurDataModals {
  /**
   * The Quill component
   */
  @ViewChild('quillComponent') quillComponent?: QuillComponent;

  /**
   * The current wiki
   */
  public wiki$ = this.ourDataService.pageFragment$.pipe(
    // Get the wiki Object
    map((data) => new Wiki(data?.wiki) || new Wiki()),
    // Update the form and quill
    map((wiki) => {
      // console.log('wiki', wiki);
      // Set the form values to ensure validation
      this.form.patchValue({
        id: wiki.id,
        page: wiki.page,
        fragment: wiki.fragment,
        content: JSON.stringify(wiki.content || '{}'),
      });

      if (wiki.content) {
        // Set the quill content
        this.quillComponent?.quillInstance?.setContents(wiki.content, 'api');
      } else {
        // Clear the quill content
        this.quillComponent?.quillInstance?.setContents({ ops: [] } as unknown as Delta, 'api');
      }

      return wiki;
    }),
  );

  /**
   * The form to use for validation
   */
  public form = this.formBuilder.nonNullable.group({
    id: new FormControl<null | number>(null, { validators: [Validators.required] }),
    page: new FormControl<null | string>(null, { validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)] }),
    fragment: new FormControl<null | string>(null, { validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)] }),
    content: new FormControl<null | string>(null, { validators: [Validators.required, Validators.minLength(26), Validators.maxLength(65535)] }),
  });

  constructor(
    private http: HttpClient,
    public ourDataService: OurDataService,
    private formBuilder: FormBuilder,
  ) {
    super(ourDataService);
    this.name = 'WikiComponent';
  }

  /**
   * The Quill editor has changed so we need to update the form
   * @param {Delta} delta - The new delta
   */
  public quillChanged(delta: Delta): void {
    this.form.controls.content.setValue(JSON.stringify(delta));
  }
  /**
   * Save the wiki
   * @param {Wiki} wiki - The wiki to save
   */
  public async save(wiki: Wiki) {
    // Force type any to set payload content to string
    const payload: any = { ...wiki, content: this.quillComponent?.quillInstance?.getContents() };

    // Set the content to a string
    payload.content = JSON.stringify(payload.content);

    // Set the form values to ensure validation
    this.form.patchValue({ ...payload });

    if (!this.form.valid) {
      return;
    }

    // Save the wiki
    return await firstValueFrom(this.http.put<Wiki>(`${environment.api_url}/advisor-portal/wiki`, payload)).then((data) => {
      const pageFragment = this.ourDataService.pageFragment$.value;
      if (pageFragment?.wiki?.content) {
        pageFragment.wiki.content = data.content;
        this.ourDataService.pageFragment$.next(pageFragment);
      }
    });
  }
}

export class Wiki extends DatabaseRecord() {
  /**
   * The current page
   */
  public page = '';

  /**
   * The current fragment
   */
  public fragment = '';

  /**
   * The content of the wiki
   */
  public content?: Delta;

  constructor(data?: Wiki) {
    super(data);
    Object.assign(this, data);
    // Parse the content if it is a string
    if (data?.content) this.content = data.content;
  }
}
